import { TeamsFxContext } from "@/Context";
import { useErrorDialog } from "@/components/error/ErrorDialogContext";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { useContext } from "react";
import type { SWRConfiguration } from "swr";
import useSWR, { SWRResponse } from "swr";
import useSWRMutation, { SWRMutationConfiguration, SWRMutationResponse } from "swr/mutation";

// axiosのデフォルト設定
export const defaultAxiosConfig: AxiosRequestConfig = {
  timeout: 60000,
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true, // クッキーを送信するために必要
};

// axiosインスタンスの作成
const axiosInstance = axios.create(defaultAxiosConfig);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    // The error will be handled by the hooks, so we just pass it through
    return Promise.reject(error);
  },
);

/**
 * デフォルト設定(ハードコード)
 */
export const defaultSWROptions = {
  dedupingInterval: 1000 * 60 * 60,
  shouldRetryOnError: false,
  revalidateOnFocus: false,
  onError: (e: unknown) => {
    console.error(e);
  },
};

/**
 * APIへのGETを行うためのカスタムフック
 * 使用時は型情報を指定し以下のように使う
 *
 * ```ts
 * const { data, error, isLoading, isValidating, mutate } = useAPI<
 *     ResponseModel, //ResponseBodyの型
 *     OutputModel // 戻り値のモデルの型
 * >(
 *   "/api/endpoint",
 *   {"param1": "value1", "param2": "value2"},
 *   {
 *     // SWRのオプション(オーバーライドしたい時)
 *   },
 *   {
 *     // axiosのオプション(オーバーライドしたい時)
 *   }
 * );
 * ```
 * @param options
 */
export const useAPI = <TRes, TOut = TRes>(
  url: string,
  responseToModel: (res: TRes) => TOut,
  swrOptions?: Partial<SWRConfiguration>,
  axiosConfig?: AxiosRequestConfig,
): SWRResponse<TOut> => {
  // axiosを使ったAPIリクエストのfetcher関数を定義
  // 内部でresponseとモデルの変換を行う
  const { token } = useContext(TeamsFxContext);
  const { showError } = useErrorDialog();
  const fetcher = async (): Promise<TOut> => {
    return axiosInstance({
      method: "GET",
      url: url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      ...axiosConfig,
    })
      .then((res) => responseToModel(res.data))
      .catch((error: AxiosError) => {
        if (error.response && (error.response.status === 400 || error.response.status >= 500)) {
          showError();
        }
        throw error;
      });
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    url,
    fetcher,
    { ...defaultSWROptions, ...swrOptions },
  );
  return { data, error, isLoading, isValidating, mutate };
};

/** SWRResponseのうちuseAPI()では固定となる部分を省略した型 */
export type APIResponse<TOut> = SWRResponse<TOut, AxiosError>;

/**
 * APIへのPOST/PUT/DELETEを行うためのカスタムフック
 * SWRとaxiosを組み合わせて、APIリクエストを簡単に行えるようしてある
 * 使用時は型情報を指定し以下のように使う
 *
 * ```ts
 * const { data, error, isLoading, isValidating, mutate } = useAPI<
 *     RequestModel, //RequestBodyの型
 *     ResponseModel, //ResponseBodyの型
 *     InputModel, // リクエストボディのモデルの型
 *     OutputModel // 戻り値のモデルの型
 * >(
 *   "/api/endpoint", // APIのURL
 *   {"param1": "value1", "param2": "value2"}, // リクエストボディ
 *   modelToRequest, // リクエストボディをモデルから変換する関数,
 *   responseToModel, // レスポンスをモデルに変換する関数
 *   {
 *     // SWRのオプション(オーバーライドしたい時)
 *   },
 *   {
 *     // axiosのオプション(オーバーライドしたい時)
 *   }
 * );
 * ```
 * @param options
 */
export const useAPIMutation = <TReq, TRes, TIn = TReq, TOut = TRes>(
  method: "POST" | "PUT" | "DELETE",
  cacheKey: string,
  modelToRequest: (model: TIn) => TReq,
  responseToModel: (res: TRes) => TOut,
  swrOptions?: SWRMutationConfiguration<TOut, AxiosError, string, TIn>,
  axiosConfig?: AxiosRequestConfig<TReq>,
): SWRMutationResponse<TOut, AxiosError, string, TIn> => {
  // axiosを使ったAPIリクエストのfetcher関数を定義
  // 内部でモデルとrequest/responseの変換を行う
  // HEAD
  //
  const { token } = useContext(TeamsFxContext);
  const { showError } = useErrorDialog();
  // origin/main
  const fetcher = (defaultUrl: string, { arg }: Readonly<{ arg: TIn; }>): Promise<TOut> => {
    const url = defaultUrl;
    const body = modelToRequest(arg);
    return axiosInstance(
      {
        method: method,
        url: url,
        data: body,
        headers: {
          // HEAD
          Authorization: `Bearer ${token}`,
        }, // origin/main
        ...axiosConfig,
      },
    ).then(
      (res: AxiosResponse<TRes>) => responseToModel(res.data),
      (error: AxiosError) => {
        if (error.response && (error.response.status === 400 || error.response.status >= 500)) {
          showError();
        }
        throw error;
      },
    );
  };

  // SWRのMutationを使用して各変数を取得
  const result = useSWRMutation<TOut, AxiosError, string, TIn>(
    cacheKey,
    fetcher,
    { ...defaultSWROptions, ...swrOptions },
  );
  return result;
};

/** SWRMutationResponseのうちuseAPIMutation()では固定となる部分を省略した型 */
export type APIMutationResponse<TOut, TIn> = SWRMutationResponse<TOut, AxiosError, string, TIn>;
